<template>
  <div>
    <!--
      プレゼンテーショナルコンポーネントを呼び出します。
      v-bindで全てのデータと算出プロパティをpropsとして渡し、
      v-onで全てのイベントを監視します。
    -->
    <DeveloperSettingsView
      v-bind="viewProps"
      v-on="viewEvents"
    />

    <!--
      モーダルの表示制御とイベントハンドリングはコンテナが担当します。
      v-modelで表示状態を双方向バインディングし、@confirmなどのイベントを待ち受けます。
    -->
    <TextModal
      modal-id="confirm-clear-modal"
      title="位置情報設定クリア"
      message="位置情報設定をクリアしてもよろしいですか？"
      cancel-text="キャンセル"
      confirm-text="OK"
      header-style="gradient"
      v-model="locationManagement.modals.confirmClear.visible"
      @confirm="handleClearConfirm"
    />

    <TextModal
      modal-id="confirm-account-delete-modal"
      title="アカウント削除"
      :message="accountManagement.accountDeleteMessage.value"
      cancel-text="キャンセル"
      confirm-text="削除"
      header-style="gradient"
      v-model="accountManagement.modals.accountDelete.visible"
      @confirm="handleAccountDeleteConfirm"
    />

    <AccountEditModal
      :visible="accountManagement.modals.accountEdit.visible"
      :account="accountManagement.modals.accountEdit.account"
      @close="accountManagement.modals.accountEdit.visible = false"
      @save="handleAccountEditSave"
    />

    <AccountCreateModal
      :visible="accountManagement.modals.accountCreate.visible"
      @close="accountManagement.modals.accountCreate.visible = false"
      @save="handleAccountCreateSave"
    />

    <CoordinateEditModal
      ref="coordinateEditModal"
      :visible="locationManagement.modals.coordinateEdit.visible"
      :value="locationManagement.coordinateOffset"
      @input="locationManagement.modals.coordinateEdit.visible = $event"
      @save="handleCoordinateSave"
    />

    <LocationConfirmModal
      ref="locationConfirmModal"
      v-model="locationManagement.modals.locationConfirm.visible"
      :facility-name="locationManagement.pendingLocationChange.value ? locationManagement.pendingLocationChange.value.facilityName : ''"
      :original-position="locationManagement.pendingLocationChange.value ? locationManagement.pendingLocationChange.value.originalPosition : {}"
      :new-position="locationManagement.pendingLocationChange.value ? locationManagement.pendingLocationChange.value.newPosition : {}"
      @confirm="handleLocationConfirm"
      @cancel="locationManagement.handleLocationCancel"
    />
  </div>
</template>

<script setup>
/**
 * DeveloperSettingsContainer - 開発者向け設定コンテナ (Composition API)
 * 
 * Composition APIとcomposablesを使用した現代的な実装。
 * ロジックをuseAccountManagementとuseLocationManagementに分離し、
 * このコンテナはUIの統合とイベントハンドリングに専念します。
 */
import { ref, reactive, computed, onMounted } from 'vue'
import DeveloperSettingsView from '../components/DeveloperSettingsView.vue'
import TextModal from '../../../components/molecules/TextModal.vue'
import AccountEditModal from '../components/modals/AccountEditModal.vue'
import AccountCreateModal from '../components/modals/AccountCreateModal.vue'
import CoordinateEditModal from '../components/modals/CoordinateEditModal.vue'
import LocationConfirmModal from '@/components/organisms/LocationConfirmModal.vue'
import { useAccountManagement } from '../composables/useAccountManagement.js'
import { useLocationManagement } from '../composables/useLocationManagement.js'

// Composablesを使用
const accountManagement = useAccountManagement()
const locationManagement = useLocationManagement()

// タブ設定
const tabs = ref([
  { key: 'info_settings', title: '各種情報設定' },
  { key: 'account_settings', title: 'アカウント設定' },
])
const activeTab = ref('info_settings')

// 設定データ
const settings = reactive({
  username: '',
})

// エラー状態
const error = ref(null)

// 初期ローディング状態
const loading = reactive({
  initial: false,
  facilities: locationManagement.loading.facilities,
  coordinateCorrection: locationManagement.loading.coordinateCorrection,
  accounts: accountManagement.loading.accounts,
})

/**
 * 初期データを取得
 */
const loadInitialData = async () => {
  loading.initial = true
  error.value = null

  try {
    console.log('[Container] 初期データの取得を開始')

    // 並行してデータを取得
    const [facilities, coordinateCorrection] = await Promise.all([
      locationManagement.loadFacilities(),
      locationManagement.loadCoordinateCorrection()
    ])

    locationManagement.locationData.value = facilities
    locationManagement.coordinateOffset.value = coordinateCorrection

    console.log('[Container] 初期データの取得完了', {
      facilities: facilities.length,
      coordinateCorrection
    })

  } catch (err) {
    console.error('[Container] 初期データの取得に失敗:', err)
    error.value = err.message
  } finally {
    loading.initial = false
  }
}

/**
 * タブ変更処理
 */
const handleTabChange = (tabKey) => {
  activeTab.value = tabKey
}

/**
 * エラーメッセージをクリア
 */
const handleClearError = () => {
  error.value = null
}

/**
 * 設定更新処理
 */
const handleUpdateSettings = (newSettings) => {
  Object.assign(settings, newSettings)
}

/**
 * アカウント設定保存
 */
const saveAccountSettings = () => {
  console.log('アカウント設定を保存:', settings.username)
}

// アカウント管理のラッパーメソッド
const handleAccountCreateSave = async (accountData) => {
  const err = await accountManagement.handleAccountCreateSave(accountData)
  if (err) error.value = err
}

const handleAccountEditSave = async (accountData) => {
  const err = await accountManagement.handleAccountEditSave(accountData)
  if (err) error.value = err
}

const handleAccountDeleteConfirm = async () => {
  const err = await accountManagement.handleAccountDeleteConfirm()
  if (err) error.value = err
}

// 位置情報管理のラッパーメソッド
const handleClearConfirm = async () => {
  const err = await locationManagement.handleClearConfirm()
  if (err) error.value = err
}

const handleCoordinateSave = async (coordinateData) => {
  const err = await locationManagement.handleCoordinateSave(coordinateData)
  if (err) error.value = err
}

const handleLocationConfirm = async () => {
  const err = await locationManagement.handleLocationConfirm()
  if (err) error.value = err
}

// Viewに渡すためのpropsをまとめる
const viewProps = computed(() => {
  return {
    tabs: tabs.value,
    activeTab: activeTab.value,
    settings: settings,
    isEditMode: locationManagement.isEditMode.value,
    currentEditingLocation: locationManagement.currentEditingLocation.value,
    locationData: locationManagement.locationData.value,
    editMapMarkers: locationManagement.editMapMarkers.value,
    editMapCenter: locationManagement.editMapCenter.value,
    editMapZoom: locationManagement.editMapZoom.value,
    accounts: accountManagement.accounts.value,
    accountHeaders: accountManagement.accountHeaders.value,
    accountActionButtons: accountManagement.accountActionButtons.value,
    formattedAccounts: accountManagement.formattedAccounts.value,
    error: error.value,
    loading: loading,
  }
})

// Viewから受け取るイベントをまとめる
const viewEvents = computed(() => {
  return {
    'change-tab': handleTabChange,
    'update-settings': handleUpdateSettings,
    'edit-location': locationManagement.handleLocationEdit,
    'clear-location': locationManagement.handleLocationClear,
    'open-coordinate-settings': locationManagement.handleCoordinateSettings,
    'save-account-settings': saveAccountSettings,
    'exit-edit-mode': locationManagement.exitEditMode,
    'map-click': locationManagement.handleMapClick,
    'marker-drag-end': locationManagement.handleMarkerDragEnd,
    'show-create-modal': accountManagement.handleShowCreateModal,
    'edit-account': accountManagement.handleEditAccount,
    'delete-account': accountManagement.handleDeleteAccount,
    'account-action': accountManagement.handleAccountAction,
    'clear-error': handleClearError,
  }
})

// 初期化
onMounted(async () => {
  await loadInitialData()
  await accountManagement.loadAccounts()
})
</script>
